import {
  Attachment,
  Coding,
  Meta,
  QuestionnaireItemTypeEnum,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  QuestionnaireResponseStatusEnum,
  Questionnaire,
  QuestionnaireItemAnswerOption,
  QuestionnaireItem,
} from '@core/fhir-r4/fhir';
import type {
  TriageAnswerRecord,
  TriageAnswerValue,
  TriageAttachmentAnswer,
  TriageQuestion,
  TriageQuestionnaire,
} from '@core/entities/Triage';

const wholeNumberPattern = /^[+-]?\d+$/;
const sdcQuestionnaireResponseProfileUrl = 'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaireresponse';

export class R4QuestionnaireResponseBuilder {
  public build(questionnaire: Questionnaire | TriageQuestionnaire, answers: TriageAnswerRecord): QuestionnaireResponse {
    const fhirQuestionnaire = this.isTriageQuestionnaire(questionnaire)
      ? this.createQuestionnaireForSubmission(questionnaire)
      : questionnaire;

    return this.buildFromQuestionnaire(fhirQuestionnaire, answers);
  }

  private buildFromQuestionnaire(questionnaire: Questionnaire, answers: TriageAnswerRecord): QuestionnaireResponse {
    const items = (questionnaire.item ?? [])
      .map((item) => this.mapItem(item, answers))
      .filter((item): item is QuestionnaireResponseItem => item !== null);

    return new QuestionnaireResponse({
      authored: new Date().toISOString(),
      meta: new Meta({
        profile: [sdcQuestionnaireResponseProfileUrl],
      }),
      questionnaire: this.resolveQuestionnaireReference(questionnaire),
      status: QuestionnaireResponseStatusEnum.COMPLETED,
      item: items.length > 0 ? items : undefined,
    });
  }

  private isTriageQuestionnaire(questionnaire: Questionnaire | TriageQuestionnaire): questionnaire is TriageQuestionnaire {
    return 'questions' in questionnaire;
  }

  private createQuestionnaireForSubmission(questionnaire: TriageQuestionnaire): Questionnaire {
    return new Questionnaire({
      id: questionnaire.id,
      item: questionnaire.questions.map((question) => this.mapQuestion(question)),
    });
  }

  private mapQuestion(question: TriageQuestion): QuestionnaireItem {
    switch (question.type) {
      case 'group-single':
      case 'group-multiple':
        return new QuestionnaireItem({
          linkId: question.linkId,
          text: question.text,
          type: QuestionnaireItemTypeEnum.GROUP,
          required: question.required,
          item: (question.children ?? []).map((child) => this.mapQuestion(child)),
        });
      case 'single':
      case 'categorical-single-choice':
      case 'multiple-choice':
      case 'symptoms':
      case 'list':
        return new QuestionnaireItem({
          linkId: question.linkId,
          text: question.text,
          type: QuestionnaireItemTypeEnum.CHOICE,
          required: question.required,
          repeats: question.type === 'multiple-choice' || question.type === 'list',
          answerOption: (question.options ?? []).map((option) => new QuestionnaireItemAnswerOption({
            valueCoding: {
              code: option.code,
              display: option.label,
              ...(option.system ? { system: option.system } : {}),
            },
          })),
        });
      case 'boolean':
        return new QuestionnaireItem({
          linkId: question.linkId,
          text: question.text,
          type: QuestionnaireItemTypeEnum.BOOLEAN,
          required: question.required,
        });
      case 'slider':
        return new QuestionnaireItem({
          linkId: question.linkId,
          text: question.text,
          type: question.numericFormat === 'decimal'
            ? QuestionnaireItemTypeEnum.DECIMAL
            : QuestionnaireItemTypeEnum.INTEGER,
          required: question.required,
        });
      case 'free-text':
        return new QuestionnaireItem({
          linkId: question.linkId,
          text: question.text,
          type: QuestionnaireItemTypeEnum.STRING,
          required: question.required,
        });
      case 'image-upload':
        return new QuestionnaireItem({
          linkId: question.linkId,
          text: question.text,
          type: QuestionnaireItemTypeEnum.ATTACHMENT,
          required: question.required,
        });
      default:
        throw new Error(`Question type '${question.type}' is not supported by the canonical triage response builder.`);
    }
  }

  private resolveQuestionnaireReference(questionnaire: Questionnaire): string | undefined {
    const canonicalUrl = questionnaire.url?.trim();
    if (canonicalUrl) {
      if (canonicalUrl.startsWith('#') || this.isAbsoluteUrl(canonicalUrl)) {
        return canonicalUrl;
      }
    }

    const questionnaireId = questionnaire.id?.trim();
    return questionnaireId ? `#${questionnaireId}` : undefined;
  }

  private isAbsoluteUrl(candidate: string): boolean {
    try {
      return Boolean(new URL(candidate).protocol);
    } catch {
      return false;
    }
  }

  private mapItem(item: QuestionnaireItem, answers: TriageAnswerRecord): QuestionnaireResponseItem | null {
    if (!item.linkId || !item.type) {
      throw new Error('The triage questionnaire contains an invalid question definition.');
    }

    const responseItem = {
      linkId: item.linkId,
      text: item.text,
    };

    switch (item.type) {
      case QuestionnaireItemTypeEnum.GROUP:
      {
        const children = (item.item ?? [])
          .map((child) => this.mapItem(child, answers))
          .filter((child): child is QuestionnaireResponseItem => child !== null);

        if (children.length === 0) {
          return null;
        }

        return new QuestionnaireResponseItem({
          ...responseItem,
          item: children,
        });
      }
      case QuestionnaireItemTypeEnum.CHOICE:
      {
        const rawAnswers = this.normalizeChoiceAnswers(item, answers[item.linkId]);
        if (rawAnswers.length === 0) {
          return null;
        }

        return new QuestionnaireResponseItem({
          ...responseItem,
          answer: rawAnswers.map((rawAnswer) => this.mapChoiceAnswer(item, rawAnswer)),
        });
      }
      case QuestionnaireItemTypeEnum.INTEGER:
      {
        const rawAnswer = this.normalizeScalarAnswer(answers[item.linkId]);
        if (rawAnswer === null) {
          return null;
        }

        return new QuestionnaireResponseItem({
          ...responseItem,
          answer: [this.mapIntegerAnswer(item, rawAnswer)],
        });
      }
      case QuestionnaireItemTypeEnum.DECIMAL:
      {
        const rawAnswer = this.normalizeScalarAnswer(answers[item.linkId]);
        if (rawAnswer === null) {
          return null;
        }

        return new QuestionnaireResponseItem({
          ...responseItem,
          answer: [this.mapDecimalAnswer(item, rawAnswer)],
        });
      }
      case QuestionnaireItemTypeEnum.BOOLEAN:
      {
        const rawAnswer = this.normalizeBooleanAnswer(answers[item.linkId]);
        if (rawAnswer === null) {
          return null;
        }

        return new QuestionnaireResponseItem({
          ...responseItem,
          answer: [new QuestionnaireResponseItemAnswer({ valueBoolean: rawAnswer })],
        });
      }
      case QuestionnaireItemTypeEnum.STRING:
      case QuestionnaireItemTypeEnum.TEXT:
      {
        const rawAnswer = this.normalizeScalarAnswer(answers[item.linkId]);
        if (rawAnswer === null) {
          return null;
        }

        return new QuestionnaireResponseItem({
          ...responseItem,
          answer: [new QuestionnaireResponseItemAnswer({ valueString: rawAnswer })],
        });
      }
      case QuestionnaireItemTypeEnum.ATTACHMENT:
      {
        const rawAnswer = this.normalizeAttachmentAnswer(answers[item.linkId]);
        if (rawAnswer === null) {
          return null;
        }

        return new QuestionnaireResponseItem({
          ...responseItem,
          answer: [new QuestionnaireResponseItemAnswer({
            valueAttachment: new Attachment(rawAnswer),
          })],
        });
      }
      default:
        throw new Error(`Question type '${item.type}' is not supported by the canonical triage response builder.`);
    }
  }

  private normalizeChoiceAnswers(item: QuestionnaireItem, rawAnswer: TriageAnswerValue | undefined): readonly string[] {
    if (rawAnswer === undefined) {
      return [];
    }

    if (Array.isArray(rawAnswer)) {
      return rawAnswer.map((value) => value.trim()).filter((value) => value.length > 0);
    }

    if (typeof rawAnswer === 'string') {
      const normalized = rawAnswer.trim();
      return normalized ? [normalized] : [];
    }

    if (typeof rawAnswer === 'number' || typeof rawAnswer === 'boolean') {
      return [`${rawAnswer}`];
    }

    throw new TypeError(`Question '${item.linkId}' requires coded answer values.`);
  }

  private normalizeScalarAnswer(rawAnswer: TriageAnswerValue | undefined): string | null {
    if (rawAnswer === undefined) {
      return null;
    }

    if (typeof rawAnswer === 'string') {
      const normalized = rawAnswer.trim();
      return normalized || null;
    }

    if (typeof rawAnswer === 'number' || typeof rawAnswer === 'boolean') {
      return `${rawAnswer}`;
    }

    if (!Array.isArray(rawAnswer)) {
      throw new TypeError('Scalar answers must be a string, number, boolean, or list of strings.');
    }

    if (rawAnswer.some((value) => typeof value !== 'string')) {
      throw new TypeError('Scalar answers must be a string, number, boolean, or list of strings.');
    }

    return rawAnswer.length > 0 ? rawAnswer[0]?.trim() ?? null : null;
  }

  private normalizeBooleanAnswer(rawAnswer: TriageAnswerValue | undefined): boolean | null {
    if (rawAnswer === undefined) {
      return null;
    }

    if (typeof rawAnswer === 'boolean') {
      return rawAnswer;
    }

    if (typeof rawAnswer === 'string') {
      const normalized = rawAnswer.trim().toLowerCase();
      if (normalized === 'true' || normalized === 'yes') {
        return true;
      }

      if (normalized === 'false' || normalized === 'no') {
        return false;
      }
    }

    throw new TypeError('Boolean answers must be true, false, yes, or no.');
  }

  private normalizeAttachmentAnswer(rawAnswer: TriageAnswerValue | undefined): TriageAttachmentAnswer | null {
    if (rawAnswer === undefined) {
      return null;
    }

    if (!this.isAttachmentAnswer(rawAnswer)) {
      throw new TypeError('Attachment answers must provide a file title and file content or URL.');
    }

    if (!rawAnswer.title.trim() || (!rawAnswer.data?.trim() && !rawAnswer.url?.trim())) {
      throw new TypeError('Attachment answers must provide a file title and file content or URL.');
    }

    return rawAnswer;
  }

  private isAttachmentAnswer(rawAnswer: TriageAnswerValue): rawAnswer is TriageAttachmentAnswer {
    return typeof rawAnswer === 'object' && rawAnswer !== null && !Array.isArray(rawAnswer) && 'title' in rawAnswer;
  }

  private mapChoiceAnswer(item: QuestionnaireItem, rawAnswer: string): QuestionnaireResponseItemAnswer {
    const option = item.answerOption?.find((candidate) =>
      candidate.valueCoding?.code === rawAnswer
      || candidate.valueString === rawAnswer
      || `${candidate.valueInteger ?? ''}` === rawAnswer,
    );

    if (!option) {
      throw new Error(`No matching answer option was found for question '${item.linkId}'.`);
    }

    if (option.valueCoding) {
      return new QuestionnaireResponseItemAnswer({
        valueCoding: new Coding(option.valueCoding),
      });
    }

    if (option.valueString) {
      return new QuestionnaireResponseItemAnswer({
        valueString: option.valueString,
      });
    }

    if (option.valueInteger !== undefined) {
      return new QuestionnaireResponseItemAnswer({
        valueInteger: option.valueInteger,
      });
    }

    throw new Error(`The selected answer option for question '${item.linkId}' is not supported.`);
  }

  private mapIntegerAnswer(item: QuestionnaireItem, rawAnswer: string): QuestionnaireResponseItemAnswer {
    if (!wholeNumberPattern.test(rawAnswer)) {
      throw new TypeError(`Question '${item.linkId}' requires a whole number answer.`);
    }

    const parsed = Number(rawAnswer);

    if (!Number.isInteger(parsed)) {
      throw new TypeError(`Question '${item.linkId}' requires a whole number answer.`);
    }

    return new QuestionnaireResponseItemAnswer({
      valueInteger: parsed,
    });
  }

  private mapDecimalAnswer(item: QuestionnaireItem, rawAnswer: string): QuestionnaireResponseItemAnswer {
    const parsed = Number.parseFloat(rawAnswer);

    if (Number.isNaN(parsed)) {
      throw new TypeError(`Question '${item.linkId}' requires a numeric answer.`);
    }

    return new QuestionnaireResponseItemAnswer({
      valueDecimal: parsed,
    });
  }
}